/**
 * ImageUploadField
 * A field component for uploading images to page content sections.
 * Supports drag-and-drop, click to upload, and displays image preview.
 */

import { useState, useCallback } from 'react'
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

interface ImageUploadFieldProps {
  value: string
  onChange: (url: string) => void
  accountId: string
  fieldKey: string
  placeholder?: string
  disabled?: boolean
  label?: string
  description?: string
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const BUCKET_NAME = 'page-content-images'

export function ImageUploadField({
  value,
  onChange,
  accountId,
  fieldKey,
  placeholder,
  disabled = false,
  label,
  description,
}: ImageUploadFieldProps) {
  const { toast } = useToast()
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const uploadImage = useCallback(
    async (file: File) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast({
          title: 'Error',
          description: 'Invalid file type. Please use PNG, JPG, or WebP.',
          variant: 'destructive',
        })
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Error',
          description: 'File too large. Maximum size is 5MB.',
          variant: 'destructive',
        })
        return
      }

      setIsUploading(true)

      try {
        // Delete old image if exists and is from our bucket
        if (value && value.includes(BUCKET_NAME)) {
          try {
            const url = new URL(value)
            const pathParts = url.pathname.split(`/storage/v1/object/public/${BUCKET_NAME}/`)
            if (pathParts[1]) {
              const cleanPath = pathParts[1].split('?')[0]
              await supabase.storage.from(BUCKET_NAME).remove([cleanPath])
            }
          } catch {
            // Ignore errors when removing old image
          }
        }

        const fileExt = file.name.split('.').pop()
        const timestamp = Date.now()
        const fileName = `${accountId}/${fieldKey}-${timestamp}.${fileExt}`

        const { error: uploadError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
          })

        if (uploadError) throw uploadError

        const {
          data: { publicUrl },
        } = supabase.storage.from(BUCKET_NAME).getPublicUrl(fileName)

        // Add cache busting parameter
        const urlWithCacheBust = `${publicUrl}?t=${timestamp}`
        onChange(urlWithCacheBust)
        toast({ title: 'Image uploaded successfully' })
      } catch (error) {
        console.error('Upload error:', error)
        toast({
          title: 'Error',
          description: 'Failed to upload image',
          variant: 'destructive',
        })
      } finally {
        setIsUploading(false)
      }
    },
    [accountId, fieldKey, onChange, value, toast]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        uploadImage(file)
      }
      // Reset input so the same file can be selected again
      e.target.value = ''
    },
    [uploadImage]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragging(false)

      const file = e.dataTransfer.files?.[0]
      if (file) {
        uploadImage(file)
      }
    },
    [uploadImage]
  )

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
    },
    [onChange]
  )

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <div className="space-y-3">
        {/* Image preview and upload area */}
        {value ? (
        <div className="relative">
          <div className="relative aspect-video w-full max-w-md overflow-hidden rounded-lg border bg-muted">
            <img
              src={value}
              alt="Background preview"
              className="h-full w-full object-cover"
              onError={(e) => {
                // Hide broken image and show placeholder
                e.currentTarget.style.display = 'none'
              }}
            />
          </div>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute right-2 top-2 h-8 w-8"
            onClick={() => {
              // Delete from storage if it's from our bucket (fire and forget)
              if (value && value.includes(BUCKET_NAME)) {
                supabase.storage
                  .from(BUCKET_NAME)
                  .remove([value.split(`${BUCKET_NAME}/`)[1]?.split('?')[0] || ''])
                  .catch(console.error)
              }
              onChange('')
              toast({ title: 'Image removed' })
            }}
            disabled={disabled}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div
          className={cn(
            'relative flex aspect-video w-full max-w-md cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/50',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            onChange={handleFileChange}
            disabled={disabled || isUploading}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
          {isUploading ? (
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
          ) : (
            <>
              {isDragging ? (
                <Upload className="h-10 w-10 text-primary" />
              ) : (
                <ImageIcon className="h-10 w-10 text-muted-foreground" />
              )}
              <p className="mt-2 text-sm text-muted-foreground">
                {isDragging ? 'Drop image here' : 'Click or drag to upload'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                PNG, JPG, WebP up to 5MB
              </p>
            </>
          )}
        </div>
      )}

        {/* URL input for manual entry */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">or enter URL:</span>
          <Input
            type="url"
            value={value}
            onChange={handleUrlChange}
            placeholder={placeholder || 'https://example.com/image.jpg'}
            disabled={disabled}
            className="flex-1"
          />
        </div>
      </div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  )
}
